import { useEffect, useRef, useState } from 'react'
import useMediaQuery from 'hooks/use-media-query'

interface VideoBackgroundProps {
  src: string
  poster?: string
  className?: string
  // Optional desktop swap: when the viewport matches desktopMinWidth the
  // component plays desktopSrc/desktopPoster instead (art direction).
  desktopSrc?: string
  desktopPoster?: string
  desktopMinWidth?: number
}

const VideoBackground = ({
  src,
  poster,
  className,
  desktopSrc,
  desktopPoster,
  desktopMinWidth = 1024
}: VideoBackgroundProps) => {
  const [hasError, setHasError] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const isDesktop = useMediaQuery(`(min-width: ${desktopMinWidth}px)`)

  // Asset filenames contain spaces/brackets (CDN mp4 names): always encode.
  const rawSrc = isDesktop && desktopSrc ? desktopSrc : src
  const rawPoster = isDesktop && desktopPoster ? desktopPoster : poster
  const activeSrc = encodeURI(rawSrc)
  const activePoster = rawPoster ? encodeURI(rawPoster) : undefined

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    // React 17 does not render the muted attribute; set it manually so the
    // browser autoplay policy allows playback to start.
    video.muted = true
    video.setAttribute('muted', '')
    video.play().catch(() => undefined)
  }, [hasError, activeSrc])

  if (hasError && activePoster) {
    return <img src={activePoster} alt="" className={className} />
  }

  return (
    <video
      ref={videoRef}
      key={activeSrc}
      className={className}
      src={activeSrc}
      poster={activePoster}
      autoPlay
      muted
      loop
      playsInline
      onError={() => setHasError(true)}
    />
  )
}

export default VideoBackground
